<script>
  import Button from '@components/buttons/button.svelte'
  import Image from '@components/general/image.svelte'
  import Text from '@components/general/text.svelte'
  import Tag from '@components/general/tag.svelte'
  import ContributorBlock from '@components/contributors/info-block.svelte'
  import { Download, ExternalLink, Lock } from 'lucide-svelte'
  import { colors } from '@lib/utils/colors'
  import { cn } from '@lib/utils'
  import { filesize } from 'filesize'
  import ago from 's-ago'
  import { page } from '@inertiajs/svelte'

  let { resources = [] } = $props()

  let user = $page.props.user
  let locked = (resource) => !resource.free && (!user.is_admin && !user?.has_active_membership)

  function downloadResource(e, resource) {
    e?.preventDefault?.()
    e?.stopPropagation?.()
    if (resource?.file?.url) window.open(resource.file.url, '_blank')
  }
</script>

<div class="grid gap-10 grid-cols-[repeat(auto-fill,minmax(25rem,1fr))]">
  {#each resources as resource (resource.id)}
    {@const color = resource.color || 'gray'}

    <Button
      variant="naked"
      path={`/resources/${resource.id}`}
      class={cn('flex flex-col gap-2 group items-start w-full ring-2 ring-offset-4', colors[color]?.ring, colors[color]?.hover_ring)}
    >
      <!-- The card/image area -->
      <div class={cn('w-full rounded-sm shadow-md aspect-video relative flex items-center justify-center', colors[color]?.gradient, locked(resource) ? 'grayscale opacity-50' : '')}>

        {#if locked(resource)}
          <span aria-hidden="true" class="pointer-events-none absolute inset-0 z-10 bg-gray-900/20"></span>
          <Lock size={32} class="absolute left-1/2 top-1/2 z-20 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/90 p-2 text-gray-400" />
        {:else}
          <!-- Action button top-left -->
          {#if resource.resource_type === 'link'}
            <Button
              variant="naked"
              Icon={ExternalLink}
              class="absolute top-3 left-3 bg-white/80 rounded-full p-2 text-gray-600 shadow-sm hover:text-gray-700 z-20"
              hint="Visit resource"
              externalPath={resource.url}
            />
            <div class="absolute bottom-3 right-3 bg-white/80 px-1 py-0.5 rounded-sm text-gray-500 text-xs shadow-sm">
              External Link
            </div>
          {:else if resource.file?.url}
            <Button
              variant="naked"
              Icon={Download}
              class="absolute top-3 left-3 bg-white/80 rounded-full p-2 text-gray-600 shadow-sm hover:text-gray-700 z-20"
              hint="Download resource"
              onclick={(e) => downloadResource(e, resource)}
            />
            {#if resource.file?.byte_size}
              <div class="absolute bottom-3 right-3 bg-white/80 px-1 py-0.5 rounded-sm text-gray-500 text-xs shadow-sm">
                {resource.file.extension?.toUpperCase()} · {filesize(resource.file.byte_size)}
              </div>
            {/if}
          {/if}
        {/if}

        <!-- Horizontal line -->        
        <div class="absolute bottom-[30%] left-0 right-0 h-px bg-gray-200"></div>

        <!-- Cover image or white placeholder card -->
        {#if resource.cover_image}
          <Image src={resource.cover_image.thumb_url} alt={resource.cover_image.name} width={100} height={100} class="rounded-sm shadow-md h-[80%] w-[34%] z-10" />
        {:else}
          <div class="bg-white rounded-sm shadow-md p-4 h-[70%] w-[30%] z-10"></div>
        {/if}

      </div>

      <!-- The details -->
      <div class="flex flex-col gap-2 items-start p-2 w-full">
        <Text variant="section_title" text={resource.name} />

        <div class="flex flex-wrap gap-1">
          {#each resource.tags ?? [] as tag (tag.id)}
            <Tag {tag} size="xs" class="rounded-md px-[6px]" variant="faint" path={`/resources?tag=${tag.id}`} />
          {:else}
            <Tag tag={{ name: "Untagged", color: "gray" }} size="xs" class="rounded-md px-[6px]" variant="faint" />
          {/each}
        </div>

        {#if resource.created_at}
          <span class="text-gray-500 text-xs">Added {ago(new Date(resource.created_at))}</span>
        {/if}

        {#if resource.contributor}
          <ContributorBlock contributor={resource.contributor} size="xs" class="w-full border-none px-0 pt-2 pb-0">
            {#snippet title()}
              <span class="text-gray-500 text-xs leading-none">By</span>
            {/snippet}
            {#snippet subtitle()}
              <span class="text-gray-700 font-medium text-smaller leading-none">{resource.contributor.name}</span>
            {/snippet}
          </ContributorBlock>
        {/if}
      </div>
    </Button>
  {:else}
    <div class="col-span-full bg-stone-50 border border-dashed border-stone-200 flex items-center justify-center h-80 text-stone-500 text-sm rounded-md">
      No resources found
    </div>
  {/each}
</div>
